import React, { useState } from 'react';
import { InspectionSchedule, InspectionTemplate } from '../../services/inspectionsApi';
import { ContactGroup } from '../../services/alertsApi';
import { ui } from '../../styles/shared';

/** Redigerare för ett schema: mall, plats, intervall, ansvarig. */

export const UNIT_LABELS: Record<string, [string, string]> = {
    day: ['dag', 'dagar'],
    week: ['vecka', 'veckor'],
    month: ['månad', 'månader'],
    quarter: ['kvartal', 'kvartal'],
    year: ['år', 'år'],
};

export const intervalText = (unit: string, count: number): string => {
    const [one, many] = UNIT_LABELS[unit] ?? [unit, unit];
    return count === 1 ? `varje ${one}` : `var ${count}:e ${many}`;
};

// Stilar från den gemensamma stilfilen (styles/shared.ts → ui).
const input = ui.input;
const label = ui.label;
const btn = (bg: string): React.CSSProperties => ({ ...ui.btnPrimary, backgroundColor: bg });

interface Props {
    schedule: InspectionSchedule | null;
    templates: InspectionTemplate[];
    zones: { id: number; name: string; kind?: 'zone' | 'area'; parent_id?: number | null; child_count?: number }[];
    contacts: { id: number; name: string; email?: string | null }[];
    groups: ContactGroup[];
    onSave: (s: Partial<InspectionSchedule>) => Promise<void>;
    onCancel: () => void;
}

const today = () => new Date().toLocaleDateString('sv-SE');

// Ansvarig väljs i en lista med både grupper och personer. Värdet kodas som "g:<id>" eller "c:<id>".
const responsibleValue = (s: InspectionSchedule | null): string =>
    s?.responsible_group_id ? `g:${s.responsible_group_id}` : s?.responsible_contact_id ? `c:${s.responsible_contact_id}` : '';
const escalationValue = (s: InspectionSchedule | null): string =>
    s?.escalation_group_id ? `g:${s.escalation_group_id}` : s?.escalation_contact_id ? `c:${s.escalation_contact_id}` : '';
const idOf = (v: string, prefix: 'g:' | 'c:'): number | null => (v.startsWith(prefix) ? Number(v.slice(2)) : null);

/** Hur många platser valet blir: ett område ger sina zoner, en zon ger sig själv (samma regel som servern). */
const countPlaces = (zones: Props['zones'], chosen: number[]): number => {
    const out = new Set<number>();
    for (const id of chosen) {
        const z = zones.find(x => x.id === id);
        if (!z) continue;
        if (z.kind === 'area') zones.filter(c => c.kind !== 'area' && c.parent_id === z.id).forEach(c => out.add(c.id));
        else out.add(z.id);
    }
    return out.size;
};

const ScheduleEditor: React.FC<Props> = ({ schedule, templates, zones, contacts, groups, onSave, onCancel }) => {
    // Platsväljaren i två steg: välj område (eller "utan område") först, sedan hela området eller enskilda zoner.
    const areas = zones.filter(z => z.kind === 'area').sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    const looseZones = zones.filter(z => z.kind !== 'area' && !z.parent_id).sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    const [pickArea, setPickArea] = useState<string>(() => areas.length ? `a:${areas[0].id}` : 'loose');
    const pickedArea = pickArea.startsWith('a:') ? areas.find(a => a.id === Number(pickArea.slice(2))) ?? null : null;
    const zonesInPick = pickedArea
        ? zones.filter(z => z.kind !== 'area' && z.parent_id === pickedArea.id).sort((a, b) => a.name.localeCompare(b.name, 'sv'))
        : looseZones;
    const [form, setForm] = useState({
        name: schedule?.name ?? '',
        template_id: schedule?.template_id ?? (templates[0]?.id ?? 0),
        zone_ids: schedule?.zone_ids?.length ? schedule.zone_ids : (schedule?.zone_id ? [schedule.zone_id] : []),
        interval_unit: schedule?.interval_unit ?? 'month',
        interval_count: schedule?.interval_count ?? 1,
        start_date: schedule?.start_date ?? today(),
        remind_days_before: schedule?.remind_days_before ?? 3,
        grace_days: schedule?.grace_days ?? 0,
        responsible: responsibleValue(schedule),
        escalation: escalationValue(schedule),
        escalation_on_deviation: schedule?.escalation_on_deviation ?? false,
        active: schedule?.active ?? true,
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const set = (patch: Partial<typeof form>) => setForm(prev => ({ ...prev, ...patch }));
    const wholeAreaChosen = !!pickedArea && form.zone_ids.includes(pickedArea.id);
    const toggleZone = (id: number, on: boolean) => set({ zone_ids: on ? [...form.zone_ids, id] : form.zone_ids.filter(x => x !== id) });
    const nameOf = (id: number) => zones.find(z => z.id === id)?.name ?? `#${id}`;

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (!form.name.trim()) { setError('Schemat måste ha ett namn'); return; }
        if (!form.template_id) { setError('Välj en mall'); return; }
        setSaving(true);
        try {
            await onSave({
                ...form,
                name: form.name.trim(),
                zone_id: form.zone_ids.length === 1 ? form.zone_ids[0] : null,
                zone_ids: form.zone_ids,
                responsible_group_id: idOf(form.responsible, 'g:'),
                responsible_contact_id: idOf(form.responsible, 'c:'),
                escalation_group_id: idOf(form.escalation, 'g:'),
                escalation_contact_id: idOf(form.escalation, 'c:'),
                escalation_on_deviation: !!form.escalation && form.escalation_on_deviation,
            } as Partial<InspectionSchedule>);
        } catch (err: any) {
            setError(err.message || 'Kunde inte spara schemat');
        } finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} style={{ background: 'white', border: '1px solid #ddd', borderRadius: 8, padding: 20, marginBottom: 20 }}>
            <h2 style={{ marginTop: 0 }}>{schedule ? 'Ändra schema' : 'Nytt schema'}</h2>
            {error && <div style={{ background: '#f8d7da', color: '#721c24', padding: 10, borderRadius: 4, marginBottom: 12 }}>{error}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <div>
                    <label style={label}>Namn</label>
                    <input value={form.name} onChange={e => set({ name: e.target.value })} placeholder="T.ex. Brandskydd kök" style={input} />
                </div>
                <div>
                    <label style={label}>Mall</label>
                    <select value={form.template_id} onChange={e => set({ template_id: Number(e.target.value) })} style={input}>
                        {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                </div>
                <div>
                    <label style={label}>Platser (valfritt)</label>
                    {zones.length === 0 ? (
                        <div style={{ fontSize: 13, color: '#888' }}>Inga zoner finns. Skapa dem under Zoner.</div>
                    ) : (
                        <div style={{ border: '1px solid #ccc', borderRadius: 6, padding: 10, background: 'white' }}>
                            {/* Valda platser: områden och zoner blandat, som etiketter med kryss */}
                            {form.zone_ids.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                                    {form.zone_ids.map(id => {
                                        const z = zones.find(x => x.id === id);
                                        const isArea = z?.kind === 'area';
                                        return (
                                            <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '3px 8px', borderRadius: 12,
                                                background: isArea ? '#e8f0fe' : '#eef6f0', color: isArea ? '#1a4aa6' : '#1b5e20', border: `1px solid ${isArea ? '#1a4aa6' : '#28a745'}` }}>
                                                {isArea ? `Hela ${nameOf(id)}` : nameOf(id)}
                                                <button type="button" onClick={() => toggleZone(id, false)} title="Ta bort"
                                                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'inherit', padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                            {/* Steg 1: område */}
                            {areas.length > 0 && (
                                <select value={pickArea} onChange={e => setPickArea(e.target.value)} style={{ ...input, marginBottom: 8 }}>
                                    {areas.map(a => <option key={a.id} value={`a:${a.id}`}>{a.name} (område, {a.child_count ?? 0} {(a.child_count ?? 0) === 1 ? 'zon' : 'zoner'})</option>)}
                                    {looseZones.length > 0 && <option value="loose">Zoner utan område</option>}
                                </select>
                            )}
                            {/* Steg 2: hela området eller enskilda zoner i det */}
                            <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                                {pickedArea && (
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, padding: '3px 0', cursor: 'pointer', fontWeight: 600 }}>
                                        <input type="checkbox" checked={wholeAreaChosen} onChange={e => toggleZone(pickedArea.id, e.target.checked)} />
                                        Hela {pickedArea.name} <span style={{ fontSize: 12, color: '#1a4aa6', fontWeight: 400 }}>alla {zonesInPick.length} {zonesInPick.length === 1 ? 'zon' : 'zoner'}, även nya som läggs till senare</span>
                                    </label>
                                )}
                                {zonesInPick.length === 0 && <div style={{ fontSize: 13, color: '#888', padding: '3px 0' }}>Inga zoner här än.</div>}
                                {zonesInPick.map(z => {
                                    const included = wholeAreaChosen;
                                    return (
                                        <label key={z.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, padding: '3px 0', paddingLeft: pickedArea ? 18 : 0, cursor: included ? 'default' : 'pointer', color: included ? '#999' : undefined }}>
                                            <input type="checkbox" checked={included || form.zone_ids.includes(z.id)} disabled={included}
                                                onChange={e => toggleZone(z.id, e.target.checked)} />
                                            {z.name}{included && <span style={{ fontSize: 12 }}>ingår i hela området</span>}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                        {(() => { const n = countPlaces(zones, form.zone_ids); return n >= 2
                            ? <><b>Rond:</b> {n} platser. Varje tillfälle får en kontroll per plats, med en gemensam länk och påminnelse. Ett område står för alla zoner under det, även nya som läggs till senare.</>
                            : 'Kryssa i ett område eller två eller fler zoner för att göra en rond, t.ex. alla stugor.'; })()}
                    </div>
                </div>
                <div>
                    <label style={label}>Ansvarig (får påminnelser) <a href="/contacts" style={{ fontWeight: 'normal', fontSize: 12, marginLeft: 6 }}>Hantera kontakter</a></label>
                    <select value={form.responsible} onChange={e => set({ responsible: e.target.value })} style={input}>
                        <option value="">Ingen</option>
                        {groups.length > 0 && (
                            <optgroup label="Grupper (alla får länken)">
                                {groups.map(g => <option key={`g:${g.id}`} value={`g:${g.id}`}>{g.name} ({g.members.length} pers.)</option>)}
                            </optgroup>
                        )}
                        <optgroup label="Personer">
                            {contacts.map(c => <option key={`c:${c.id}`} value={`c:${c.id}`}>{c.name}{c.email ? ` (${c.email})` : ''}</option>)}
                        </optgroup>
                    </select>
                    {form.responsible.startsWith('g:') && (
                        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Alla i gruppen får påminnelsen. Den som påbörjar kontrollen gör klart den; de andra ser vem som håller på.</div>
                    )}
                </div>
                <div>
                    <label style={label}>Hur ofta</label>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span>var</span>
                        <input type="number" min={1} value={form.interval_count} onChange={e => set({ interval_count: Math.max(1, Number(e.target.value) || 1) })} style={{ ...input, width: 70 }} />
                        <select value={form.interval_unit} onChange={e => set({ interval_unit: e.target.value as any })} style={input}>
                            {Object.entries(UNIT_LABELS).map(([k, [, many]]) => <option key={k} value={k}>{many}</option>)}
                        </select>
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Kontrollen görs {intervalText(form.interval_unit, form.interval_count)}.</div>
                </div>
                <div>
                    <label style={label}>Första gången</label>
                    <input type="date" value={form.start_date} onChange={e => set({ start_date: e.target.value })} style={input} />
                </div>
                <div>
                    <label style={label}>Påminn dagar innan</label>
                    <input type="number" min={0} value={form.remind_days_before} onChange={e => set({ remind_days_before: Math.max(0, Number(e.target.value) || 0) })} style={input} />
                </div>
                <div>
                    <label style={label}>Dagar efter innan den räknas som försenad</label>
                    <input type="number" min={0} value={form.grace_days} onChange={e => set({ grace_days: Math.max(0, Number(e.target.value) || 0) })} style={input} />
                </div>
                <div>
                    <label style={label}>Andra kontakt om den inte görs i tid</label>
                    <select value={form.escalation} onChange={e => set({ escalation: e.target.value })} style={input}>
                        <option value="">Ingen</option>
                        {groups.length > 0 && (
                            <optgroup label="Grupper (alla får mejlet)">
                                {groups.map(g => <option key={`g:${g.id}`} value={`g:${g.id}`}>{g.name} ({g.members.length} pers.)</option>)}
                            </optgroup>
                        )}
                        <optgroup label="Personer">
                            {contacts.map(c => <option key={`c:${c.id}`} value={`c:${c.id}`}>{c.name}{c.email ? ` (${c.email})` : ''}</option>)}
                        </optgroup>
                    </select>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Får ett mejl när kontrollen räknas som försenad.</div>
                    {form.escalation !== '' && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 13 }}>
                            <input type="checkbox" checked={form.escalation_on_deviation} onChange={e => set({ escalation_on_deviation: e.target.checked })} />
                            Får också kopia vid avvikelse
                        </label>
                    )}
                </div>
            </div>

            {schedule && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 14 }}>
                    <input type="checkbox" checked={form.active} onChange={e => set({ active: e.target.checked })} /> Schemat är igång
                </label>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                <button type="submit" disabled={saving} style={btn('#28a745')}>{saving ? 'Sparar…' : 'Spara schema'}</button>
                <button type="button" onClick={onCancel} style={btn('#6c757d')}>Avbryt</button>
            </div>
        </form>
    );
};

export default ScheduleEditor;
